/**
 * useSession — the auth feature's hook for the self/identity surface.
 *
 * The live identity + effective-capability bundle is already resolved by the auth layer through the
 * single session seam (`@/auth/sessionProvider`: SESSION_ENDPOINT / CAPABILITIES_ENDPOINT) and held
 * in the auth store (seeded at login, degrade-by-default when those endpoints are not yet
 * implemented server-side). This hook is the feature-facing read of that state plus a live re-fetch
 * action wired to the store's `refreshCapabilities()` (which re-pulls the same seam).
 *
 * No server domain data is copied into Zustand by this feature: it reads the auth store (ephemeral
 * session/identity) and exposes refresh as an imperative action. The auth store is the canonical
 * holder of the session bundle, so there is no second source of truth.
 */
import { useCallback } from "react";
import { useAuthStore } from "@/auth";
import { tokenStore } from "@/api";
import { decodeAccessClaims } from "@/auth";

export interface SessionView {
  /** Identity (FR display fields). */
  id: string;
  email: string;
  nomComplet: string;
  laboratoireId: string;
  estProprietaire: boolean;
  presets: string[];
  /** Lab reference (null until the iam identity surface ships a name). */
  labId: string | null;
  labNom: string | null;
  /** Effective capability set (server-authoritative; empty = deny-by-default). */
  capabilities: ReadonlyArray<string>;
  /** Token metadata for the "session active" panel (display only, never a trust boundary). */
  tokenPresent: boolean;
  tokenExpiresAt: number | null;
  tokenExpiringSoon: boolean;
}

/** Read the current session as a flat view for the profile/session screens. */
export function useSessionView(): SessionView | null {
  const user = useAuthStore((s) => s.user);
  const lab = useAuthStore((s) => s.lab);
  const capsSet = useAuthStore((s) => s.capabilities);

  if (!user) return null;

  const expiresAt = tokenStore.getExpiresAt();
  return {
    id: user.id,
    email: user.email,
    nomComplet: user.nomComplet,
    laboratoireId: user.laboratoireId,
    estProprietaire: user.estProprietaire,
    presets: user.presets,
    labId: lab?.id ?? null,
    labNom: lab?.nom ?? null,
    capabilities: [...capsSet].sort((a, b) => a.localeCompare(b)),
    tokenPresent: tokenStore.getAccessToken() != null,
    tokenExpiresAt: expiresAt,
    tokenExpiringSoon: tokenStore.isExpiredOrExpiring(),
  };
}

/** The auth status, for distinguishing authenticated / loading / error / expired-idle. */
export function useSessionStatus() {
  return useAuthStore((s) => s.status);
}

/** Imperative live re-fetch of identity + capabilities (re-pulls the session seam). */
export function useRefreshSession(): () => Promise<void> {
  const refreshCapabilities = useAuthStore((s) => s.refreshCapabilities);
  return useCallback(async () => {
    await refreshCapabilities();
  }, [refreshCapabilities]);
}

/** Decoded (unverified) token claims — for display fallback when identity fields are blank. */
export function useTokenClaims() {
  return decodeAccessClaims(tokenStore.getAccessToken());
}
